import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import PageFooter from "@/components/landing/PageFooter";
import { openCalendly } from "../lib/calendly";
import { getSortedStories } from "../data/testimonialStories";

/**
 * Visszajelzések — fotóalapú történetgaléria + hosszú szöveges levél.
 *
 *  - Új történetek: /app/frontend/src/data/testimonialStories.js
 *  - Képek: /app/frontend/public/images/testimonials/<id>/01.jpg, 02.jpg, ...
 *  - A lightbox a képeket vágás nélkül mutatja (object-contain).
 *  - Billentyűzet (←/→/Esc) és érintéses lapozás támogatott.
 */
export default function Testimonials() {
  const stories = getSortedStories();
  const [activeStory, setActiveStory] = useState(null);

  return (
    <main
      data-testid="testimonials-page"
      className="bg-warm text-graphite min-h-screen"
    >
      <Header />
      <div className="pt-24 md:pt-28" />

      {/* HERO */}
      <section
        data-testid="testimonials-hero"
        className="px-6 md:px-10 py-14 md:py-20"
      >
        <div className="max-w-2xl mx-auto text-center">
          <h1
            data-testid="testimonials-hero-title"
            className="font-serif-display text-[42px] sm:text-[52px] md:text-[62px] leading-[1.1] text-graphite tracking-tight mb-8"
          >
            Vissza
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              jelzések
            </em>
          </h1>
          <p className="font-serif-display italic text-graphite text-[19px] md:text-[22px] leading-[1.7] max-w-xl mx-auto">
            Történetek azoktól, akik már megtapasztalták
            <br className="hidden md:block" />
            a közös munka folyamatát.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="hairline" />
      </div>

      {/* PHOTO STORIES GRID (or soft placeholder when empty) */}
      {stories.length > 0 ? (
        <section
          data-testid="story-grid"
          className="px-5 md:px-10 pt-10 md:pt-14 pb-8 md:pb-10"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {stories.map((story) => (
                <StoryCard
                  key={story.id}
                  story={story}
                  onOpen={() => setActiveStory(story)}
                />
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section
          data-testid="story-grid-empty"
          className="px-6 md:px-10 py-14 md:py-20"
        >
          <div className="max-w-xl mx-auto text-center">
            <div className="border border-hairline bg-tinted px-8 md:px-10 py-12 md:py-14">
              <p className="font-body text-[11px] md:text-[12px] tracking-[0.42em] uppercase text-gold mb-5">
                — Történetek hamarosan
              </p>
              <p className="font-serif-display italic text-graphite text-[18px] md:text-[20px] leading-[1.65]">
                A fotóalapú visszajelzések publikálása
                <br className="hidden md:block" />
                folyamatban van.
              </p>
              <p className="mt-5 font-body font-light text-[14px] md:text-[15px] text-graphite-soft leading-[1.75]">
                Addig is olvasd el a hosszabb levelet alább.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* TEXT TESTIMONIAL — Heni (preserved) */}
      <TestimonialLetter
        testId="testimonial-heni"
        label="Heni visszajelzése"
        author="Heni"
        teaser="„Nem hiszem el, hogy csak és kizárólag a szenvedésen keresztül tanulhatunk, fejlődhetünk és teljesedhetünk ki.”"
        preview={
          <>
            <p>
              Még mindig az a bizonyos júniusi tanfolyam, ahol megismertem Enikőt. Már
              korábban is jelentkeztem a programjára, ahol gyakorlókat keresett, de többször
              betelt, mikorra észrevettem. A mai napon megértettem, hogy miért. Kellett a
              tér, főleg Nekem, hogy mögé lássak a működésének, hogy érezzem, hol van az Ő
              helye akár az én, akár a kollektív mátrixban.
            </p>
            <p>
              Írtam már arról, hogy sokáig a miérteket kerestem, hisz az általam ismert
              modalitások ezekre adtak <em>„választ”</em>, de sajnos engem személy szerint
              ez nem vitt előre, sőt sok mindent általánosságnak tartottam. A miértek
              keresése bezárt egy dobozba és ezen szűk keretek közül kellett volna
              újjáépíteni Önmagam.
            </p>
          </>
        }
      >
        <p>
          Viszont a kvantum kinyitott egy teret, a minden lehetséges terét, ahol rajtad
          áll, hogy könnyedén és játékosan, vagy kissé mélyebben szeretnéd
          újrakalibrálni az életedet.
        </p>
        <p>
          Enikő képviseli a kissé mélyebben, vagy nagyon mélyen vonalat, de olyan
          játszi könnyedséggel, hogy ezt el sem tudtam képzelni. Nekem az az érzésem,
          hogy nem a szenvedőket szeretné megjavítani, hanem a szenvedést, a tragédiát
          megelőzve útjukra állítani az embereket. Ez a szemlélet nagyon összecsengett
          azzal a felismerésemmel, ami Dorinám elmenetele után erősen feljött bennem,
          hogy a rendszer maga, kicsit kisiklott.
        </p>

        <LetterQuote testId="testimonial-heni-pullquote">
            Nem hiszem el, hogy csak és kizárólag a szenvedésen keresztül tanulhatunk,
            fejlődhetünk és teljesedhetünk ki.
        </LetterQuote>

        <p>
          De akármerre mentem, mindenhol az fogadott, hogy majd meglátod, hogy mennyi
          ajándék van ebben, a szenvedéssel fejlődik a tudatod, ezt Te választottad
          leszületésedkor. Hiszem, hogy ezekben is vannak igazságok, nem is kívánok
          semmi tanításnak ellene menni, viszont nem is tudtam azonosulni vele. De ez
          természetesen mindenkinek egyéni.
        </p>
        <p>
          Enikő módszere a minden egy, a forrás energiájával dolgozik, ahol
          észrevesszük eddigi mintázatainkat, lenyomatainkat és ezek feloldásával segít
          a saját forrásod felfedezésében. Olyan ez, mint mikor gyermekként még
          érzékelted a jót, a mindent, majd a mátrix szépen elveszi tőled.
        </p>

        <LetterQuote testId="testimonial-heni-pullquote-2">
            De ha választod, akkor felnőttként újra kapcsolódhatsz a{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              varázslathoz
            </em>
            , ami Te magad vagy.
        </LetterQuote>

        <p>
          Az igazság az, hogy ezt iszonyatosan nehéz szavakkal kifejezni, mivel a szavak
          mögötti jelentés mindenkinek más. Egy biztos, azt érzem, hogy nagyon más
          megvilágításba kerül a szenvedéstudat ezzel a módszerrel, hisz nem szükséges
          elmenni a végsőkig ahhoz, akár generálni egy betegséget bármit, hogy
          fejlődhessen a tudatod, a lelked. Bízom benne, hogy ez átmegy mindenkinek, aki
          ezt olvassa.
        </p>
        <p>
          Az érzékeltem, hogy Enikő a nulladik pillanatban fogja meg a dolgokat
          mindenkinél, aki erre nyitott és ezzel nem klasszikusan a szenvedőknek
          hozzájárulás, hanem pont ellenkezőleg egy kicsit új nézetet kíván behozni. Mi
          az, amit szenvedés nélkül is megtanulhatok? Mi lenne, ha nem kellene átélnem
          a szenvedést ahhoz, hogy fejlődhessek az életemben? Mi lenne, ha átírnánk a
          kollektívban lévő szenvedőké a mennyek országa kifejezést.
        </p>
      </TestimonialLetter>

      {/* CLOSING CTA */}
      <section
        data-testid="testimonials-closing"
        className="px-6 md:px-10 py-16 md:py-20 border-t border-hairline"
      >
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-serif-display italic text-graphite text-[20px] md:text-[24px] leading-[1.7]">
            Ha te is szeretnéd megélni,
            <br className="hidden md:block" />
            miről szólnak ezek a szavak,
            <br />
            várlak szeretettel{" "}
            <button
              type="button"
              onClick={openCalendly}
              data-testid="testimonials-cta-consultation"
              className="text-gold border-b border-gold hover:opacity-80 transition-opacity duration-300 bg-transparent p-0 inline cursor-pointer italic"
              style={{ font: "inherit" }}
            >
              egyéni konzultációra
            </button>
            ,
            <br className="hidden md:block" />
            vagy a{" "}
            <Link
              to="/elmenyprogram"
              data-testid="testimonials-cta-group"
              className="text-gold border-b border-gold hover:opacity-80 transition-opacity duration-300"
              style={{ fontStyle: "italic" }}
            >
              csoportos alkalmak
            </Link>{" "}
            egyikére.
          </p>
        </div>
      </section>

      <PageFooter />

      {/* LIGHTBOX */}
      {activeStory && (
        <StoryLightbox
          story={activeStory}
          onClose={() => setActiveStory(null)}
        />
      )}
    </main>
  );
}

/* ---------------------------------------------------------------------- */
/* StoryCard — a single person's story preview                            */
/* ---------------------------------------------------------------------- */
function StoryCard({ story, onOpen }) {
  const coverIdx = story.coverIndex ?? 0;
  const items = story.items || story.images || [];
  const cover = items[coverIdx];
  const count = items.length;
  const title = story.title || `${story.name} története`;
  const hasVideo = items.some((it) => it?.type === "video");
  const coverSrc = cover?.type === "video" ? cover.poster : cover?.src;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`story-card-${story.id}`}
      aria-label={`${title} — ${count} bejegyzés`}
      className="group flex flex-col text-left bg-warm border border-hairline hover:border-gold transition-colors duration-300 overflow-hidden focus:outline-none focus:border-gold"
      style={{ boxShadow: "0 10px 40px -32px rgba(160, 122, 40, 0.3)" }}
    >
      <div className="relative overflow-hidden" style={{ aspectRatio: "4 / 5" }}>
        {coverSrc ? (
          <img
            src={coverSrc}
            alt={cover?.alt || title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            style={{ objectPosition: "center top" }}
          />
        ) : (
          <div className="w-full h-full bg-tinted flex items-center justify-center text-muted-warm text-[13px] italic font-serif-display">
            Nincs borítókép
          </div>
        )}

        {/* Play icon overlay when the story contains any video */}
        {hasVideo && (
          <span
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <span className="w-14 h-14 md:w-16 md:h-16 bg-warm/95 border border-gold rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105">
              <span
                className="block"
                style={{
                  width: 0,
                  height: 0,
                  borderTop: "9px solid transparent",
                  borderBottom: "9px solid transparent",
                  borderLeft: "14px solid var(--gold, #a07a28)",
                  marginLeft: "4px",
                }}
              />
            </span>
          </span>
        )}

        {count > 1 && (
          <span className="absolute bottom-3 right-3 bg-warm/90 border border-hairline px-3 py-1 font-body text-[10.5px] tracking-[0.3em] uppercase text-graphite">
            {count} {hasVideo ? "videó" : "kép"}
          </span>
        )}
      </div>

      <div className="px-6 md:px-7 py-6 md:py-7 flex-1 flex flex-col">
        <h3 className="font-serif-display text-graphite text-[22px] md:text-[24px] leading-[1.3]">
          {title}
        </h3>
        {story.subtitle && (
          <p className="mt-2 font-serif-display italic text-graphite-soft text-[14.5px] md:text-[15.5px] leading-[1.6]">
            {story.subtitle}
          </p>
        )}
        <p className="mt-6 pt-5 border-t border-hairline font-body text-[11px] md:text-[11.5px] tracking-[0.28em] uppercase text-gold group-hover:text-graphite transition-colors duration-300 flex items-center gap-2">
          Történet megtekintése <span aria-hidden="true">→</span>
        </p>
      </div>
    </button>
  );
}

/* ---------------------------------------------------------------------- */
/* StoryLightbox — full-screen photo / video viewer                       */
/* ---------------------------------------------------------------------- */
function StoryLightbox({ story, onClose }) {
  const items = story.items || story.images || [];
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const videoRef = useRef(null);

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);
  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Pause any playing video when the user navigates between items
  useEffect(() => {
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (_) {
        /* no-op */
      }
    }
  }, [index]);

  // Touch (swipe) navigation
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    if (touchStartX.current == null) return;
    const dx = touchDeltaX.current;
    if (Math.abs(dx) > 40) {
      if (dx > 0) goPrev();
      else goNext();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  const current = items[index];
  const count = items.length;
  const isVideo = current?.type === "video";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${story.name} története — ${index + 1} / ${count}`}
      data-testid="story-lightbox"
      className="fixed inset-0 z-[60] bg-[rgba(32,28,24,0.92)] backdrop-blur-md flex flex-col"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar — title + counter + close */}
      <div className="flex items-center justify-between px-5 md:px-10 py-4 md:py-5 text-warm border-b border-white/10">
        <div className="min-w-0">
          <p className="font-serif-display italic text-[14px] md:text-[16px] text-warm/80 leading-tight truncate">
            {story.name} története
          </p>
          <p
            data-testid="story-lightbox-counter"
            className="font-body text-[10.5px] md:text-[11px] tracking-[0.35em] uppercase text-warm/60 mt-0.5"
          >
            {index + 1} / {count}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="story-lightbox-close"
          aria-label="Bezárás"
          className="w-11 h-11 flex items-center justify-center text-warm/80 hover:text-gold transition-colors duration-300"
        >
          <span className="relative block w-5 h-5" aria-hidden="true">
            <span className="absolute inset-0 top-1/2 h-px bg-current rotate-45" />
            <span className="absolute inset-0 top-1/2 h-px bg-current -rotate-45" />
          </span>
        </button>
      </div>

      {/* Media area */}
      <div className="relative flex-1 flex items-center justify-center px-3 md:px-20 py-4 md:py-8">
        {/* Prev button — desktop */}
        {count > 1 && (
          <button
            type="button"
            onClick={goPrev}
            data-testid="story-lightbox-prev"
            aria-label="Előző"
            className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center border border-white/20 text-warm/80 hover:text-gold hover:border-gold transition-colors duration-300"
          >
            <span aria-hidden="true" className="text-xl leading-none">
              ‹
            </span>
          </button>
        )}

        {current && isVideo && (
          <video
            key={`${story.id}-${index}`}
            ref={videoRef}
            src={current.src}
            poster={current.poster}
            data-testid="story-lightbox-video"
            controls
            controlsList="nodownload"
            playsInline
            preload="metadata"
            className="max-w-full max-h-[78vh] md:max-h-[82vh] bg-black select-none"
            style={{ aspectRatio: current.aspectRatio }}
          />
        )}

        {current && !isVideo && (
          <img
            key={`${story.id}-${index}`}
            src={current.src}
            alt={current.alt || `${story.name} visszajelzése — ${index + 1}. kép`}
            data-testid="story-lightbox-image"
            className="max-w-full max-h-[78vh] md:max-h-[82vh] object-contain select-none"
            draggable={false}
          />
        )}

        {/* Next button — desktop */}
        {count > 1 && (
          <button
            type="button"
            onClick={goNext}
            data-testid="story-lightbox-next"
            aria-label="Következő"
            className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center border border-white/20 text-warm/80 hover:text-gold hover:border-gold transition-colors duration-300"
          >
            <span aria-hidden="true" className="text-xl leading-none">
              ›
            </span>
          </button>
        )}
      </div>

      {/* Dots + mobile nav buttons */}
      {count > 1 && (
        <div className="flex items-center justify-center gap-3 px-4 pb-5 md:pb-7">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Előző"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-white/20 text-warm/80 hover:text-gold transition-colors duration-300"
          >
            <span aria-hidden="true" className="text-lg">
              ‹
            </span>
          </button>

          <div
            data-testid="story-lightbox-dots"
            className="flex items-center gap-2"
            aria-hidden="true"
          >
            {items.map((_, i) => (
              <span
                key={i}
                className={`block transition-all duration-300 ${
                  i === index
                    ? "w-6 h-[2px] bg-gold"
                    : "w-2 h-[2px] bg-white/30"
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={goNext}
            aria-label="Következő"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-white/20 text-warm/80 hover:text-gold transition-colors duration-300"
          >
            <span aria-hidden="true" className="text-lg">
              ›
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* TestimonialLetter — reusable long-form testimonial layout              */
/* --------------------------------------------------------------------- */
function LetterQuote({ testId, children, className = "!my-10 md:!my-12" }) {
  return (
    <blockquote
      data-testid={testId}
      className={`${className} mx-auto max-w-3xl`}
      style={{ textAlign: "center", hyphens: "none", WebkitHyphens: "none" }}
    >
      <span aria-hidden="true" className="block mx-auto mb-6 h-px w-14 bg-[var(--accent-gold)] opacity-60" />
      <p className="font-serif-display italic text-gold text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45]">
        {children}
      </p>
      <span aria-hidden="true" className="block mx-auto mt-6 h-px w-14 bg-[var(--accent-gold)] opacity-60" />
    </blockquote>
  );
}

function TestimonialLetter({ testId, title, label, author, teaser, preview, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const toggle = () => {
    if (open && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen((v) => !v);
  };
  return (
    <article
      ref={ref}
      data-testid={testId}
      className="relative px-6 md:px-10 pt-8 md:pt-10 pb-14 md:pb-20 border-t border-hairline scroll-mt-24"
    >
      <div className="max-w-5xl mx-auto">
        {title && (
          <div
            aria-hidden="true"
            className="font-serif-display italic text-gold text-[70px] md:text-[90px] leading-none mb-1 -ml-2 md:-ml-4 select-none"
            style={{ opacity: 0.7 }}
          >
            &ldquo;
          </div>
        )}

        {title && (
          <h2
            data-testid={`${testId}-title`}
            className="font-serif-display text-graphite text-[26px] sm:text-[32px] md:text-[36px] leading-[1.25] mb-8 md:mb-10"
          >
            {title}
          </h2>
        )}

        {label && (
          <p
            data-testid={`${testId}-label`}
            className="font-body text-[11px] md:text-[12px] tracking-[0.38em] uppercase text-gold text-center mb-6 md:mb-7"
          >
            — {label}
          </p>
        )}

        {teaser && (
          <LetterQuote testId={`${testId}-teaser`} className="!mt-0 !mb-8 md:!mb-10">
            {teaser}
          </LetterQuote>
        )}

        <div className="reading-prose text-[16px] md:text-[17px] leading-[1.9] text-graphite font-body font-light">
          <div className="relative">
            {preview}
            {!open && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-28"
                style={{ background: "linear-gradient(180deg, rgba(249,248,246,0) 0%, #F9F8F6 92%)" }}
              />
            )}
          </div>
          <div
            id={`${testId}-rest`}
            data-testid={`${testId}-rest`}
            aria-hidden={!open}
            className={`grid transition-[grid-template-rows,opacity] duration-700 ease-out ${
              open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className={`overflow-hidden ${open ? "pt-[1.1rem]" : ""}`}>{children}</div>
          </div>
        </div>


        <div className={`${open ? "mt-12 md:mt-14" : "mt-2"} flex items-center gap-5 justify-end`}>
          <span className="h-px w-16 bg-[var(--accent-gold)]" aria-hidden="true" />
          <p
            data-testid={`${testId}-author`}
            className="font-serif-display italic text-gold text-[22px] md:text-[26px]"
          >
            {author}
          </p>
        </div>

        <div className="mt-10 md:mt-12 text-center">
          <button
            type="button"
            data-testid={`${testId}-toggle`}
            onClick={toggle}
            aria-expanded={open}
            aria-controls={`${testId}-rest`}
            className="cta-gold-outline inline-flex items-center gap-3 px-8 py-3.5 text-[11.5px] md:text-[12px] uppercase font-body font-medium tracking-[0.22em]"
          >
            {open ? "Levél összecsukása" : "A teljes levél elolvasása"}
            <span aria-hidden="true" className={`transition-transform duration-500 ${open ? "rotate-180" : ""}`}>
              ↓
            </span>
          </button>
        </div>
      </div>
    </article>
  );
}
